/**
 * 水印 —— 编辑器预览与各种导出共用。
 *
 * 为什么单独成文件：水印现在出现在四个互不相干的地方（编辑器预览的叠图层、
 * 导出 HTML 的内联样式、PDF 的 canvas 绘制、MusicXML 的来源字段），参数必须
 * 保持一致，分散写迟早会出现"编辑器里好看、导出里歪了"的情况。
 *
 * 两种落地方式，按出口选：
 *   · `watermarkOverlayCss()` —— 给 HTML/DOM 用。CSS `background-image` + SVG
 *     data URI 铺开。好处是矢量、体积小；**坏处是 html2canvas 对 SVG 背景的
 *     支持不稳定**，所以走截图的 PDF 不要用它，改用下面的 canvas 绘制。
 *   · `drawWatermarkOnCanvas()` —— 给 html2canvas 截图后的 PDF 用。直接用
 *     Canvas 2D 的 fillText 画，与 html2canvas 的兼容性完全无关，一定能进 PDF。
 */

/** 水印文案（域名随 2026-10 国内迁移换为 manmantan.xyz） */
export const WATERMARK_TEXT = '漫漫弹 https://manmantan.xyz'

/**
 * 深浅主题必须给不同参数，不能共用一套 fill-opacity：同一个透明度在宣纸浅底上
 * 刚好看得见，在夜墨深底上就直接消失（用户实测反馈）。
 */
function inkOf(isDark: boolean) {
	return isDark
		? {fill: '#e8eeec', opacity: 0.085}
		: {fill: '#000000', opacity: 0.055}
}

/**
 * 铺一张 760×440 的贴图（比最初的 460×230 稀疏得多，一行谱面约两条，用户反馈过密）。
 */
function tileSvg(text: string, isDark: boolean) {
	const { fill, opacity } = inkOf(isDark)
	return '<svg xmlns="http://www.w3.org/2000/svg" width="760" height="440" viewBox="0 0 760 440">'
		+ `<text x="380" y="225" transform="rotate(-28 380 225)" font-size="38"`
		+ ` fill="${fill}" fill-opacity="${opacity}" text-anchor="middle" font-family="sans-serif">`
		+ text
		+ '</text></svg>'
}

/**
 * CSS `background-image` 用的贴图 url。
 * 必须 encodeURIComponent：文案含中文，且 SVG 属性里有引号，不转义整条 url 会失效。
 */
export function watermarkTileUrl(text: string, isDark: boolean) {
	return `url("data:image/svg+xml;utf8,${encodeURIComponent(tileSvg(text, isDark))}")`
}

/**
 * 导出 HTML 时内联进 `<style>` 的一段水印样式。
 * 选择器挂在 body 上并用 `position:fixed` 的覆盖层 —— 导出的 HTML 页面高度随
 * 曲谱长度变化，绝对定位需要逐页重算，而固定定位一张铺满视口最省事也最稳。
 */
export function watermarkOverlayCss(text: string, isDark: boolean) {
	const fill = isDark ? '#e8eeec' : '#000000'
	const opacity = isDark ? '0.085' : '0.055'
	return `<style id="sparks-watermark">`
		+ `body{position:relative}`
		+ `.sparks-watermark{position:fixed;left:0;top:0;width:100%;height:100%;`
		+ `pointer-events:none;user-select:none;z-index:2147483000;opacity:1;`
		+ `background-image:${watermarkTileUrl(text, isDark)};background-repeat:repeat}`
		+ `@media print{.sparks-watermark{display:none}}`
		+ `</style>`
		+ `<div class="sparks-watermark" style="color:${fill};opacity:${opacity}"></div>`
}

/**
 * 在 html2canvas 截好的 canvas 上直接画水印。
 *
 * 为什么不用 DOM 叠图 + 让 html2canvas 去拍：html2canvas 对 CSS 背景里的 SVG
 * data URI 支持并不稳定，拍出来可能整层空白。这里用 Canvas 2D 直接 fillText，
 * 不经过任何 CSS 解析，是唯一能保证一定出现在 PDF 里的写法。
 */
export function drawWatermarkOnCanvas(canvas: HTMLCanvasElement, text: string, isDark: boolean) {
	// 空串 = "这个用户不该有水印"（Plus 会员）。不早退的话会照样跑完整套
	// 双重循环，每格 fillText('') —— 什么也画不出来，但几千次无意义调用。
	if(!text) {
		return
	}
	const ctx = canvas.getContext('2d')
	if(!ctx) {
		return
	}
	const { fill, opacity } = inkOf(isDark)
	// 贴图尺寸按 CSS px 定义（与 watermarkTileUrl 同一组 760×440 / 38 号字），
	// 这里统一乘 scale 换算到 canvas 像素 —— PDF 是 2 倍图，不换算水印会小一半。
	const scale = canvas.width / 794
	const stepX = 760 * scale
	const stepY = 440 * scale

	ctx.save()
	ctx.globalAlpha = opacity
	ctx.fillStyle = fill
	ctx.font = `${38 * scale}px sans-serif`
	ctx.textAlign = 'center'
	ctx.textBaseline = 'middle'
	// 文字旋转后包围盒比原网格大，从画布对角线两头各留一格起铺，保证边角也盖到
	const diag = Math.sqrt(canvas.width ** 2 + canvas.height ** 2)
	for(let y = -stepY; y < canvas.height + diag; y += stepY) {
		for(let x = -stepX; x < canvas.width + diag; x += stepX) {
			ctx.save()
			ctx.translate(x, y)
			ctx.rotate(-28 * Math.PI / 180)
			ctx.fillText(text, 0, 0)
			ctx.restore()
		}
	}
	ctx.restore()
}